import { FC, useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server';

import { MdOutlineFileDownload } from 'react-icons/md';

import Image from 'next/legacy/image';
import {
  AddToCartConfirmationModal,
  DownloadAgreementModal,
  ITeroScannerConfiguration,
  ProductImageGallery,
  WarningModal,
} from 'components';
import {
  Accordian,
  Button,
  Dropdown,
  IconButton,
  Notification,
} from 'components/core';

import {
  buildLanguageVariants,
  buildQuantityOptions,
  filterActiveSubscriptions,
  isCustomizableClamshellProduct,
  isIteroScannerProduct,
  ProductDetailsProps,
  ProductDisclaimerAPAC,
  ProductSubscriptionOptions,
  RedeemCheckbox,
  RedeemPoints,
  Styled,
  OneTimePurchasePrice,
  SubscriptionPurchasePrice,
  buildLimitExceededErrorMessage,
} from './';
import {
  useAddLineItem,
  useCreateProject,
  useToggleFavourites,
} from 'reactQuery/mutations';
import {
  useDoctorProfile,
  useFetchFavouriteProductIds,
} from 'reactQuery/queries';
import { useI18n } from 'contexts/i18n';
import { useRouter } from 'next/router';
import {
  filterBulkDiscountBySku,
  filterCouponsBySku,
  filterDSODiscountBySku,
  filterRedeemProductBySku,
  filterSubscriptionsBySku,
  isRedeemAvailable,
} from 'components/ProductTile/ProductTile.helper';
import { DesignHuddleEditor } from 'components/DesignHuddle';
import { MaterialTypeEnum } from 'types';
import { ActionType } from 'components/DownloadAgreementModal';
import { formatDateToMonthYear, isAPACStore, isZeroCurrency } from 'utils';
import Link from 'next/link';
import { useFlattenedSubscriptionHistory } from 'utils/hooks';
import { useSubscriptionProduct } from 'reactQuery/queries/subscriptions/useSubscriptionProducts';
import AvailableOffers from './AvailableOffers';
import { ProductVariantGroupSelector } from './ProductVariantGroupSelector';

export const ProductDetails: FC<ProductDetailsProps> = ({
  product,
  doctorPromotionList,
  subscriptionHistory,
}) => {
  const i18n = useI18n();
  const router = useRouter();
  const [bookmark, setBookmark] = useState(false);

  const [isCouponChecked, setIsCouponChecked] = useState(true);
  const [actionType, setActionType] = useState<ActionType>();

  const [showDownloadAgreementModal, setShowDownloadAgreementModal] =
    useState(false);
  const [showAddToCartConfirmation, setShowAddToCartConfirmation] =
    useState(false);

  const { data: favouriteProductIds } = useFetchFavouriteProductIds();
  const { data: subscriptionProductList } = useSubscriptionProduct();

  const [allowableQuantities, setAllowableQuantities] = useState(
    buildQuantityOptions(product.quantityLimit)
  );

  const [oneTimePurchaseQuantity, setOneTimePurchaseQuantity] = useState(
    allowableQuantities[0]?.value
  );

  const [subscriptionQuantity, setSubscriptionQuantity] = useState<
    undefined | string
  >();

  const [selectedFrequencyId, setSelectedFrequencyId] = useState('');

  const languageVariantsOptions = buildLanguageVariants(
    product.languageBasedVariants
  );

  const [variantId, setVariantId] = useState(
    product.languageBasedVariants.find(variant => variant.sku === product.sku)
      ?.variantId || product.variantId
  );

  const addLineItemMutation = useAddLineItem();
  const handleAddToCart = (
    isSubscriptionChecked = false,
    subscriptionFrequencyId?: string
  ) => {
    const qty = isSubscriptionChecked
      ? subscriptionQuantity
        ? parseInt(subscriptionQuantity)
        : 0
      : parseInt(oneTimePurchaseQuantity);
    addLineItemMutation.mutate({
      variantId,
      quantity: qty,
      description: couponData?.description,
      code: couponData?.code,
      setShowAddToCartConfirmation,
      isRedeemPointSelected: isRedeemChecked,
      ...(isSubscriptionChecked && {
        options: {
          subscribe: isSubscriptionChecked,
          subscription_frequency_id: subscriptionFrequencyId,
        },
      }),
    });
  };

  const toggleFavouriteMutation = useToggleFavourites();
  const handleToggleFavourites = (productID: string) => {
    toggleFavouriteMutation?.mutate({ variantId: productID.toString() });
    setBookmark(!bookmark);
  };

  const isCustomizableClamshell = isCustomizableClamshellProduct(product.sku);
  const isIteroScanner = isIteroScannerProduct(product.sku);

  const isCustomizableMarketingMaterial = product.isCustomizable;
  const isDownloadable =
    product.materialType === MaterialTypeEnum.digital ||
    product.materialType === MaterialTypeEnum.digital_physical;

  const isOrderable =
    product.materialType === MaterialTypeEnum.physical ||
    product.materialType === MaterialTypeEnum.digital_physical;

  const { data: doctorProfile } = useDoctorProfile();

  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState('');

  const createProjectMutation = useCreateProject(
    doctorProfile?.userName,
    product.sku,
    setShowCustomizationModal,
    setActiveProjectId
  );

  useEffect(() => {
    const isBookmarked =
      favouriteProductIds?.includes(product.variantId) ?? false;
    setBookmark(isBookmarked);
  }, [favouriteProductIds, product.variantId]);

  const handleVariantChange = (variantId: string) => {
    setVariantId(variantId);

    const variantSpecificQuantityLimit = product.languageBasedVariants.find(
      variant => parseInt(variant.variantId) === parseInt(variantId)
    )?.quantity_limit;

    if (variantSpecificQuantityLimit) {
      const newQuantityLimits = buildQuantityOptions(
        variantSpecificQuantityLimit
      );
      setAllowableQuantities(newQuantityLimits);
      setOneTimePurchaseQuantity(newQuantityLimits[0]?.value);
    }
  };

  const handleRedeemOnChange = () => {
    setIsRedeemChecked(prevState => !prevState);
  };

  const isFreeProduct = isZeroCurrency(product.displayPrice);
  const couponData =
    isFreeProduct || isDownloadable
      ? undefined
      : filterCouponsBySku(product.sku, doctorPromotionList);
  const bulkDiscount =
    isFreeProduct || isDownloadable
      ? undefined
      : filterBulkDiscountBySku(product.sku, doctorPromotionList);
  const dsoDiscount =
    isFreeProduct || isDownloadable
      ? undefined
      : filterDSODiscountBySku(product.sku, doctorPromotionList);
  const nonStackableOfferCount =
    (bulkDiscount ? 1 : 0) +
    (dsoDiscount ? 1 : 0) +
    (couponData && !couponData.stackable ? 1 : 0);
  const hasMultipleOffers = nonStackableOfferCount > 1;

  const isRedeemEnabled = isRedeemAvailable(
    product.sku,
    doctorPromotionList?.redeemProductList
  );
  const redeemProduct = filterRedeemProductBySku(
    product.sku,
    doctorPromotionList?.redeemProductList
  );
  const [isRedeemChecked, setIsRedeemChecked] = useState(false);

  const subscriptionProduct = filterSubscriptionsBySku(
    product.sku,
    subscriptionProductList
  );

  const flattenedSubscriptionHistory =
    useFlattenedSubscriptionHistory(subscriptionHistory);

  const isAlreadySubscribedToProduct =
    filterActiveSubscriptions(flattenedSubscriptionHistory, product.sku)
      .length > 0;

  const [isOneTimePurchaseSelected, setIsOneTimePurchaseSelected] =
    useState(true);

  const handleOneTimePurchaseChange = (value: boolean) => {
    setIsOneTimePurchaseSelected(value);
    setIsCouponChecked(value);
  };

  const onCloseEditor = () => {
    setActiveProjectId('');
    setShowCustomizationModal(false);
  };

  const onCloseAddToCartConfirmation = () => {
    setShowAddToCartConfirmation(false);
    if (isAPACStore()) {
      router.push('/');
    }
  };

  const accordianItems = [
    {
      title: i18n['Expiration Date'],
      description: formatDateToMonthYear(product.discontinueOn) ?? '',
      isExpanded: true,
    },
    {
      title: i18n['Usage'],
      description: product.instructions ?? '',
      isExpanded: true,
    },
    {
      title: i18n['Ingredients'],
      description: product.ingredients ?? '',
      isExpanded: true,
    },
  ];

  if (!isAPACStore()) {
    accordianItems.push({
      title: i18n['User Assistance'],
      description: isCustomizableMarketingMaterial
        ? ''
        : product.returnPolicy
        ? i18n[product.returnPolicy] || product.returnPolicy
        : '',
      isExpanded: false,
    });
  }

  if (!isCustomizableMarketingMaterial) {
    if (isAPACStore()) {
      accordianItems.push({
        title: i18n['Disclaimer'],
        description: ReactDOMServer.renderToStaticMarkup(
          <ProductDisclaimerAPAC
            product={product}
            countryCode={doctorProfile ? doctorProfile?.country : ''}
            i18n={i18n}
          />
        ),
        isExpanded: true,
      });
    } else {
      accordianItems.push({
        title: i18n['Disclaimer'],
        description:
          product.disclaimerText ??
          i18n[
            'Please note that the product labels may vary from those depicted here.'
          ],
        isExpanded: false,
      });
    }
  }

  const isANZDoctor =
    doctorProfile?.country === 'AU' || doctorProfile?.country === 'NZ';

  // const exceededItemsMessage = i18n['EXCEEDED_ITEMS_MESSAGE'];

  const limitExceedErrorMessage = () =>
    buildLimitExceededErrorMessage(
      addLineItemMutation?.error?.response?.data?.error,
      i18n['EXCEEDED_ITEMS_MESSAGE'] ?? ''
    );

  return (
    <>
      {isAlreadySubscribedToProduct && (
        <Notification
          title={i18n['Product is already subscribed.']}
          type="info"
          isMultiLine={false}
          marginTop="10px"
          marginBottom="10px"
          showCloseButton={true}
        >
          {i18n['To manage subscriptions']},{' '}
          <Styled.LinkWrapper>
            <Link href={'/subscribe-and-save?selected=mysubscriptions'}>
              {i18n['Click Here']}
            </Link>
          </Styled.LinkWrapper>
        </Notification>
      )}

      <Styled.ProductDetailsContainer>
        <Styled.ImageContainer>
          <ProductImageGallery images={product.images} />
        </Styled.ImageContainer>
        <Styled.DetailsContainer $isSubscription={Boolean(subscriptionProduct)}>
          <Styled.Name data-testid="product-title">{product.name}</Styled.Name>

          <Styled.Sku>
            {i18n['SKU']}: {product.sku}
          </Styled.Sku>

          {isOrderable && (
            <>
              {isAPACStore() && (
                <Styled.PriceContainer>
                  <Styled.Price
                    $shouldStrikeOff={isRedeemEnabled && isRedeemChecked}
                  >
                    {product.displayPrice}
                  </Styled.Price>
                  {isRedeemEnabled && isRedeemChecked ? (
                    <RedeemPoints redeemProduct={redeemProduct} />
                  ) : null}
                  {isANZDoctor ? (
                    <Styled.TaxText>({i18n['GST exclusive']})</Styled.TaxText>
                  ) : (
                    <Styled.TaxText>({i18n['VAT exclusive']})</Styled.TaxText>
                  )}
                </Styled.PriceContainer>
              )}
              {!isAPACStore() && (
                <>
                  {isOneTimePurchaseSelected && (
                    <OneTimePurchasePrice
                      product={product}
                      couponProduct={couponData}
                      hasMultipleOffers={hasMultipleOffers}
                    />
                  )}{' '}
                  {!isOneTimePurchaseSelected && (
                    <SubscriptionPurchasePrice
                      subscriptionProduct={subscriptionProduct}
                      selectedFrequencyId={selectedFrequencyId}
                      subscriptionQuantity={subscriptionQuantity}
                    />
                  )}
                </>
              )}
            </>
          )}
          {isOneTimePurchaseSelected && (
            <Styled.DiscountLabel>
              <AvailableOffers
                bulkDiscount={bulkDiscount}
                coupon={couponData}
                dsoDiscount={dsoDiscount}
              />
            </Styled.DiscountLabel>
          )}

          {!Boolean(subscriptionProduct) && (
            <Styled.AddToCartSection>
              <Styled.CartActions>
                {isCustomizableClamshell && (
                  <Button
                    variant="primary"
                    fullWidth={true}
                    disabled={!product.inStock || addLineItemMutation.isLoading}
                    onClick={() => {
                      router.push('/customcase');
                    }}
                    loading={addLineItemMutation.isLoading}
                    size={'md'}
                    width="80%"
                  >
                    <span>{i18n['Customize Case']}</span>
                  </Button>
                )}
                {isCustomizableMarketingMaterial && (
                  <Button
                    variant="primary"
                    size="md"
                    fullWidth={false}
                    disabled={
                      createProjectMutation.isLoading ||
                      activeProjectId.length > 0
                    }
                    onClick={() => {
                      createProjectMutation.mutate();
                    }}
                    loading={createProjectMutation.isLoading}
                  >
                    {i18n['Customize']}
                  </Button>
                )}

                {!isCustomizableClamshell &&
                  !isCustomizableMarketingMaterial &&
                  !isIteroScanner &&
                  isOrderable && (
                    <>
                      <Styled.QuantityWrapper data-testid="product-quantity">
                        <Dropdown
                          options={allowableQuantities}
                          onChange={val => {
                            setOneTimePurchaseQuantity(val);
                          }}
                          selectedValue={oneTimePurchaseQuantity}
                          label="Qty"
                          size="md"
                        ></Dropdown>
                      </Styled.QuantityWrapper>
                      <Styled.ButtonWrapper>
                        <Button
                          variant="primary"
                          disabled={
                            !product.inStock ||
                            addLineItemMutation.isLoading ||
                            oneTimePurchaseQuantity === '0' ||
                            oneTimePurchaseQuantity === undefined
                          }
                          onClick={() => handleAddToCart()}
                          loading={addLineItemMutation.isLoading}
                          size="md"
                        >
                          {product.inStock
                            ? i18n['Add to cart']
                            : i18n['Out of Stock']}
                        </Button>
                      </Styled.ButtonWrapper>
                    </>
                  )}

                {isDownloadable && !isCustomizableMarketingMaterial && (
                  <>
                    {isOrderable && (
                      <div style={{ marginLeft: '10px' }}>
                        <IconButton
                          onClick={() => {
                            setActionType('download');
                            setShowDownloadAgreementModal(true);
                          }}
                          showBorder={false}
                          size="lg"
                        >
                          <MdOutlineFileDownload />
                        </IconButton>
                      </div>
                    )}
                    {!isOrderable && (
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => {
                          setActionType('download');
                          setShowDownloadAgreementModal(true);
                        }}
                      >
                        {i18n['Download']}
                      </Button>
                    )}
                  </>
                )}
              </Styled.CartActions>

              {!isIteroScanner && (
                <Styled.Favourite>
                  {!isCustomizableClamshell && (
                    <IconButton
                      onClick={() => handleToggleFavourites(product.variantId)}
                      showBorder={false}
                      size="lg"
                    >
                      {bookmark ? (
                        <Image
                          src="/images/icons/favSelected.svg"
                          alt="favourites"
                          width="24"
                          height="24"
                        />
                      ) : (
                        <Image
                          src="/images/icons/fav.svg"
                          alt="favourites"
                          width="24"
                          height="24"
                        />
                      )}
                    </IconButton>
                  )}
                </Styled.Favourite>
              )}
            </Styled.AddToCartSection>
          )}

          {product.productGroup && product.productGroup.members.length > 1 && (
            <ProductVariantGroupSelector
              productGroup={product.productGroup}
              userCountry={doctorProfile?.country}
            />
          )}

          {isRedeemEnabled ? (
            <RedeemCheckbox
              checked={isRedeemChecked}
              onChange={handleRedeemOnChange}
            />
          ) : null}

          {product.description && (
            <Styled.Description
              dangerouslySetInnerHTML={{
                __html: product.description,
              }}
              style={{
                fontSize: '0.92rem',
                lineHeight: '24px',
                fontWeight: 400,
                fontFamily: 'Roboto',
              }}
            ></Styled.Description>
          )}
          {product.dimensions && (
            <Styled.Dimensions>
              {i18n['Dimensions']}:{product.dimensions}
            </Styled.Dimensions>
          )}

          <Styled.Accordian>
            <Accordian items={accordianItems} />
          </Styled.Accordian>
        </Styled.DetailsContainer>
        {Boolean(subscriptionProduct) && subscriptionProduct && (
          <ProductSubscriptionOptions
            product={product}
            couponProduct={couponData}
            hasMultipleOffers={hasMultipleOffers}
            subscriptionProduct={subscriptionProduct}
            addLineItemMutation={addLineItemMutation}
            handleAddToCart={handleAddToCart}
            allowableQuantities={allowableQuantities}
            setOneTimePurchaseQuantity={setOneTimePurchaseQuantity}
            oneTimePurchaseQuantity={oneTimePurchaseQuantity}
            setSubscriptionQuantity={setSubscriptionQuantity}
            subscriptionQuantity={subscriptionQuantity}
            setIsOneTimePurchaseSelected={handleOneTimePurchaseChange}
            isOneTimePurchaseSelected={isOneTimePurchaseSelected}
            selectedFrequencyId={selectedFrequencyId}
            setSelectedFrequencyId={setSelectedFrequencyId}
          />
        )}

        {isIteroScanner && <ITeroScannerConfiguration product={product} />}

        {showAddToCartConfirmation && (
          <AddToCartConfirmationModal
            name={product.name}
            price={
              isCouponChecked && couponData
                ? couponData.discountPrice
                : product.displayPrice
            }
            imageUrl={product.images[0].thumbnail}
            onClose={onCloseAddToCartConfirmation}
          />
        )}
      </Styled.ProductDetailsContainer>

      {showCustomizationModal && activeProjectId && (
        <DesignHuddleEditor
          projectId={activeProjectId}
          onCloseEditor={onCloseEditor}
          sku={product.sku}
          mode={'create'}
        />
      )}
      {showDownloadAgreementModal && (
        <DownloadAgreementModal
          onClose={() => setShowDownloadAgreementModal(false)}
          product={product}
          closeEditor={onCloseEditor}
          source="ProductPage"
          isDownloaded={false}
          isAddedToCart={false}
        />
      )}

      {addLineItemMutation.error && (
        <WarningModal
          errorMessage={limitExceedErrorMessage()}
          onClose={() => addLineItemMutation.reset()}
        />
      )}
    </>
  );
};

export default ProductDetails;
